import { BlockStack, Card, Select, Text } from "@shopify/polaris"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import type { ThemeBottomSectionState, ThemeImageBannerRef } from "@/models/ITrackingPageOptions"

import { useBackdropThemeLayout } from "./BackdropThemeLayoutContext"
import ImageUpload from "./ImageUpload"
import { mergeThemeBottomSection } from "./themeBottomSectionDefaults"
import ThemeBottomSectionEditor from "./ThemeBottomSectionEditor"

export type { ThemeLayoutBackdropContentDraft } from "./themeLayoutBackdropEditor"

const ThemeLayoutLeftContentBackdropForm = () => {
  const { t } = useTranslation("tracking_page")
  const { draft, setDraft, setBottomSectionPopupDraft } = useBackdropThemeLayout()
  const { content } = draft

  const handlePersistBanner = useCallback(
    async (next: ThemeImageBannerRef | undefined) => {
      setDraft((prev) => ({
        ...prev,
        content: { ...prev.content, image_banner: next },
      }))
    },
    [setDraft],
  )

  const commitBottomSection = useCallback(
    async (next: ThemeBottomSectionState) => {
      setDraft((prev) => ({
        ...prev,
        content: {
          ...prev.content,
          bottom_section: mergeThemeBottomSection(next),
        },
      }))
    },
    [setDraft],
  )

  const handlePersistBottomItemImage = useCallback(
    async (itemId: string, next: ThemeImageBannerRef | undefined) => {
      setDraft((prev) => {
        const prevSection = prev.content.bottom_section
        const images = prevSection.images.map((i) =>
          (i.id === itemId ? { ...i, image: next } : i),
        )

        return {
          ...prev,
          content: {
            ...prev.content,
            bottom_section: mergeThemeBottomSection({
              ...prevSection,
              images,
            }),
          },
        }
      })
    },
    [setDraft],
  )

  const handleMobileLayoutChange = useCallback(
    (value: string) => {
      setDraft((prev) => ({
        ...prev,
        content: { ...prev.content, mobile_layout: value === "HIDE" ? "HIDE" : "STACKED" },
      }))
    },
    [setDraft],
  )

  return (
    <BlockStack gap="400">
      <Card>
        <Text variant={"headingMd"} as={"h3"}>{t("TopSection")}</Text>

        <div className={"mt-4 flex flex-col gap-3"}>
          <div className={"flex flex-col gap-1"}>
            <Text variant={"bodyMd"} as={"p"}>{t("ImageBanner")}</Text>
            <div className={"mt-1"}>
              <ImageUpload
                banner={content.image_banner}
                onPersistBanner={handlePersistBanner}
              />
            </div>
          </div>

          <div>
            <Select
              label={t("mobileLayout")}
              options={[
                { value: "STACKED", label: t("Stacked") },
                { value: "HIDE", label: t("Hide") },
              ]}
              value={content.mobile_layout}
              onChange={handleMobileLayoutChange}
            />
          </div>
        </div>
      </Card>

      <Card padding={"0"}>
        <div className={"p-4"}>
          <Text variant={"headingMd"} as={"h3"}>{t("BottomSection")}</Text>
        </div>
        <ThemeBottomSectionEditor
          value={content.bottom_section}
          onCommit={commitBottomSection}
          onPersistItemImage={handlePersistBottomItemImage}
          onPopupDraftChange={setBottomSectionPopupDraft}
        />
      </Card>
    </BlockStack>
  )
}

export default ThemeLayoutLeftContentBackdropForm
